/**
 * @fileoverview Free's room for plants, where several are added at once (the seed packets
 * sheet, a photo of several kinds in Add photos).
 *
 * On Free (a trial or subscription that ended) a garden is up to a few
 * plants (the Grow setting trial.FREE_PLANTS, 5). Adding more than there is
 * room for, the button offers what fits and the rest with Grow More: "Add 2
 * now" and "Add all 6 with Grow More"; the server then adds up to the room
 * (upToRoom) and says how many were left. With no room at all (5 or more
 * growing, all of them kept), the line says what Free is and offers
 * Subscribe. Never "trial ended" or read-only: the garden is still theirs to
 * tend. A member tending someone else's garden is offered nothing to buy
 * there (useTendingFor; B23: the invitee only uses the inviter's plan): the line and "Add {room} now" stay, without
 * "Subscribe" or "Add all with Grow More".
 *
 * The room comes from the billing status (useFreeRoom), for the garden
 * being tended. Not on Free, or not known yet: unlimited, and the caller
 * shows its own button.
 *
 * Where: the seed-packet sheet (PacketReview), and in Add photos the seed
 * packets card (PacketPile) and a photo of several kinds (KindsPile).
 * API: none of its own; the caller files with upToRoom.
 * Words (en/es): billing.free.full, .room, .roomOne, .addNow,
 * .subscribeAll, .subscribe.
 *
 * @module FreeRoom
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import type { FreeRoom } from '../lib/useFreeRoom';
import { useTendingFor } from '../lib/useBilling';

/** What the choice is given. */
interface FreeRoomChoiceProps {
  /** How many plants the gardener asked to add. */
  count: number;
  /** Free's room (useFreeRoom), already known to be less than count. */
  free: FreeRoom;
  /** Adds what fits (the server adds up to the room). */
  onAdd: () => void;
  /** While filing, or while something still needs settling. */
  disabled?: boolean;
  /** Filing now: the add button shows a spinner. */
  saving?: boolean;
}

/**
 * The line and buttons for adding more plants than Free has room for: "Add
 * {room} now" and "Add all {count} with Grow More"; with no room, what Free
 * is and "Subscribe". Nothing to buy in someone else's garden.
 */
export const FreeRoomChoice: React.FC<FreeRoomChoiceProps> = ({ count, free, onAdd, disabled = false, saving = false }) => {
  const { reactTranslate: t } = useTranslation();
  /** Someone else's garden: the owner's plan, so no Subscribe (B23). */
  const tending = useTendingFor() !== null;
  if (free.room <= 0) {
    return (
      <div className="space-y-2">
        <p className="text-center text-xs text-muted-foreground" role="status">{t('billing.free.full', { limit: free.limit })}</p>
        {!tending && (
          <Button asChild className="w-full" size="lg">
            <Link to="/subscribe">{t('billing.free.subscribe')}</Link>
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <p className="text-center text-xs text-muted-foreground" role="status">
        {free.room === 1 ? t('billing.free.roomOne', { limit: free.limit }) : t('billing.free.room', { limit: free.limit, room: free.room })}
      </p>
      <Button className="w-full" size="lg" onClick={onAdd} disabled={disabled}>
        {saving ? <Spinner size={16} color="currentColor" /> : t('billing.free.addNow', { count: free.room })}
      </Button>
      {!tending && (
        <Button asChild variant="link" className="h-11 w-full">
          <Link to="/subscribe">{t('billing.free.subscribeAll', { count })}</Link>
        </Button>
      )}
    </div>
  );
};

/** The choice, as a default import. */
export default FreeRoomChoice;
